import React, { useState, useRef, useEffect } from 'react';
import { EditOutlined, DeleteOutlined, SaveOutlined, MoreOutlined } from '@ant-design/icons';
import { Dropdown, message } from 'antd';
import Button from '@mui/material/Button';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { injectIntl, FormattedMessage } from 'react-intl';
import { thunksPutActivityReportComment } from 'services/ActivityReportComment/thunks';
import { selectorsPutActivityReportComment } from 'services/ActivityReportComment/selectors';
import { bindActionCreators } from 'redux';
import { Modal } from 'antd';

const Comment = ({ comment, id, thunksPutActivityReportComment, intl }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(comment || '');
    const [inputValue, setInputValue] = useState(comment || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const textRef = useRef(null);
    const MAX_CHARS = 100;

    useEffect(
        () => {
            if (textRef.current) {
                textRef.current.style.height = 'auto';
                textRef.current.style.height = `${textRef.current.scrollHeight}px`;
            }
        },
        [text]
    );

    const handleEditClick = () => {
        setInputValue(text);
        setIsEditing(true);
    };

    const handleSaveClick = () => {
        if (inputValue.length > MAX_CHARS) {
            message.warning(intl.formatMessage({ id: 'activityreportcomment.save' }));
            return;
        }

        setIsSaving(true);

        const commentData = {
            report_id: id,
            commentaire: inputValue,
        };

        thunksPutActivityReportComment(commentData, id)
            .then(() => {
                setText(inputValue);
                setIsEditing(false);
                message.success(intl.formatMessage({ id: 'activityreportcomment.changessaved' }));
            })
            .catch((error) => {
                message.error(
                    intl.formatMessage({ id: 'activityreportcomment.failedtosavechanges' })
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    {
        /*const handleDeleteClick = () => {
        setIsDeleting(true);

        const deleteData = {
            report_id: id,
            commentaire: '',
        };

        thunksPutActivityReportComment(deleteData, id)
            .then(() => {
                setText('');
                message.success('Comment deleted successfully');
            })
            .catch((error) => {
                message.error('Failed to delete comment');
                console.error('Error deleting comment:', error);
            })
            .finally(() => {
                setIsDeleting(false);
            });
    };*/
    }

    const handleDeleteClick = () => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'activityreportcomment.confirmDelete' }),
            okText: intl.formatMessage({ id: 'activityreportcomment.yes' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'activityreportcomment.no' }),
            onOk: async () => {
                setIsDeleting(true);

                const deleteData = {
                    report_id: id,
                    commentaire: '',
                };

                try {
                    await thunksPutActivityReportComment(deleteData, id);
                    setText('');
                    message.success(
                        intl.formatMessage({
                            id: 'activityreportcomment.commentdeletedsuccessfully',
                        })
                    );
                } catch (error) {
                    message.error(
                        intl.formatMessage({ id: 'activityreportcomment.failedtodeletecomment' })
                    );
                } finally {
                    setIsDeleting(false);
                }
            },
        });
    };

    const handleInputChange = (e) => {
        if (e.target.value.length <= MAX_CHARS) {
            setInputValue(e.target.value);
        } else {
            message.warning(intl.formatMessage({ id: 'activityreportcomment.maxlengthreached' }));
        }
    };

    const items = [
        {
            label: intl.formatMessage({ id: 'activityreportcomment.edit' }),
            key: '1',
            icon: <EditOutlined />,
            onClick: handleEditClick,
        },
        {
            label: intl.formatMessage({ id: 'activityreportcomment.delete' }),
            key: '2',
            icon: <DeleteOutlined />,
            onClick: handleDeleteClick,
            disabled: isDeleting,
        },
    ];

    const baseBoxStyle = {
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        padding: '8px 12px',
        width: '130px',
        minHeight: '32px',
        backgroundColor: text ? '#fff' : '#fafafa',
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)',
        transition: 'all 0.3s',
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        overflow: 'hidden',
        fontSize: '13px',
        lineHeight: '1.4',
        ':hover': {
            borderColor: '#40a9ff',
            boxShadow: '0 0 0 2px rgba(24,144,255,0.2)',
        },
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ position: 'relative' }}>
                        <textarea
                            value={inputValue}
                            onChange={handleInputChange}
                            style={{
                                ...baseBoxStyle,
                                resize: 'none',
                                cursor: 'text',
                                fontFamily: 'inherit',
                                paddingBottom: '24px',
                            }}
                            autoFocus
                            rows={1}
                            onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                right: '8px',
                                bottom: '8px',
                                fontSize: '11px',
                                color:
                                    inputValue.length === MAX_CHARS
                                        ? '#ff4d4f'
                                        : 'rgba(0,0,0,0.45)',
                            }}
                        >
                            {inputValue.length}/{MAX_CHARS}
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <span style={{ fontSize: '11px', color: '#ff4d4f' }}>
                            {inputValue.length === MAX_CHARS
                                ? intl.formatMessage({
                                      id: 'activityreportcomment.maxlengthreached',
                                  })
                                : ''}
                        </span>
                        <Button
                            onClick={handleSaveClick}
                            disabled={isSaving}
                            style={{
                                color: 'green',
                                padding: '4px 8px',
                                fontSize: '12px',
                                minWidth: 'auto',
                            }}
                            startIcon={<SaveOutlined style={{ fontSize: '14px' }} />}
                        >
                            {intl.formatMessage({ id: 'activityreportcomment.save' })}
                        </Button>
                    </div>
                </div>
            ) : (
                <Dropdown
                    menu={{ items }}
                    trigger={['click']}
                    overlayStyle={{ minWidth: '120px' }}
                    disabled={isDeleting}
                >
                    <div
                        ref={textRef}
                        style={{
                            ...baseBoxStyle,
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            opacity: isDeleting ? 0.7 : 1,
                        }}
                    >
                        <span style={{ flex: 1 }}>
                            {text || (
                                <span style={{ color: '#bfbfbf' }}>
                                    {intl.formatMessage({
                                        id: 'activityreportcomment.addacomment',
                                    })}
                                </span>
                            )}
                        </span>
                        {!isDeleting && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                    paddingLeft: '4px',
                                }}
                            >
                                <MoreOutlined
                                    style={{
                                        color: 'rgba(0,0,0,0.45)',
                                        fontSize: '14px',
                                        transform: 'rotate(90deg)',
                                        cursor: 'pointer',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </Dropdown>
            )}
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    selectorsPutActivityReportComment: selectorsPutActivityReportComment(),
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            thunksPutActivityReportComment,
        },
        dispatch
    );

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(injectIntl(Comment))
);
